import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { Dialog } from "@angular/cdk/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faCommentSms, faEye, faPaperPlane, faSpinner } from "@fortawesome/pro-light-svg-icons";
import {
  BusinessLineOption,
  ConsumerSmsCampaignRequest,
  ConsumerSmsCampaignResult
} from "../../../models/consumer-sms-campaign.model";
import { ConsumerSmsCampaignService } from "../../../services/consumer-sms-campaign.service";
import { ConfirmationModalComponent } from "../../confirmation-modal/confirmation-modal.component";
import { ConfirmationModalData } from "../../../models/confirmation-modal-data.model";
import { ToastService } from "../../../services/toast.service";
import { StateManagementService } from "../../../services/state-management.service";

@Component({
  selector: "app-consumer-sms-campaign",
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule],
  templateUrl: "./consumer-sms-campaign.component.html"
})
export class ConsumerSmsCampaignComponent implements OnInit, OnDestroy {
  // TEMPORARY: SMS campaign is on hold pending A2P 10DLC approval — disables Preview/Send on this
  // screen as an extra UI-level safeguard on top of the CONSUMER_REFERRAL_ANNIVERSARY_SMS feature
  // flag (which already makes the backend a no-op). Flip back to false once approval comes through.
  protected readonly campaignDisabled: boolean = true;

  protected readonly faCommentSms: IconDefinition = faCommentSms;
  protected readonly faEye: IconDefinition = faEye;
  protected readonly faPaperPlane: IconDefinition = faPaperPlane;
  protected readonly faSpinner: IconDefinition = faSpinner;

  protected fromDate: string = this.today();
  protected toDate: string = this.today();
  protected readonly maxDate: string = this.today();
  protected limit: number | null = null;
  protected isPreviewing: boolean = false;
  protected isSending: boolean = false;
  protected previewResult: ConsumerSmsCampaignResult | null = null;
  protected sendResult: ConsumerSmsCampaignResult | null = null;

  protected readonly categoryOptions: { value: string; label: string }[] = [
    { value: "PERSONAL", label: "Personal" },
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "LIFE_ANNUITIES", label: "Life & Annuities" }
  ];
  protected selectedCategory: string = "";
  protected lobOptions: BusinessLineOption[] = [];
  protected selectedLobCodes: string[] = [];

  private readonly onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private consumerSmsCampaignService: ConsumerSmsCampaignService,
    private dialog: Dialog,
    private toastService: ToastService,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.stateManagementService.setPageTitle("Consumer SMS Campaign | Momentum Edge");
    this.stateManagementService.pageHeading.set("Consumer Referral SMS Campaign");
    this.loadLobOptions();
  }

  private loadLobOptions(): void {
    this.consumerSmsCampaignService
      .getBusinessLines()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (options: BusinessLineOption[]): void => {
          this.lobOptions = [...options].sort((a, b) => a.name.localeCompare(b.name));
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to load line-of-business options."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  protected get dateRangeError(): string | null {
    if (!this.fromDate || !this.toDate) {
      return null;
    }
    if (this.fromDate > this.maxDate || this.toDate > this.maxDate) {
      return "From Date and To Date cannot be in the future.";
    }
    if (this.fromDate > this.toDate) {
      return "From Date must be on or before To Date.";
    }
    return null;
  }

  protected preview(): void {
    this.isPreviewing = true;
    this.sendResult = null;
    const request: ConsumerSmsCampaignRequest = this.buildRequest(true);
    this.consumerSmsCampaignService
      .runCampaign(request)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (result: ConsumerSmsCampaignResult): void => {
          this.previewResult = result;
          this.isPreviewing = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.isPreviewing = false;
          this.previewResult = null;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to preview the campaign."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  protected confirmSend(): void {
    if (!this.previewResult) {
      return;
    }

    const data: ConfirmationModalData = {
      title: "Send Consumer Referral SMS",
      message: `Are you sure you want to send this text to ${this.previewResult.eligibleCount} referral(s)? This cannot be undone.`,
      messageHtml: `Are you sure you want to send this text to <strong>${this.previewResult.eligibleCount}</strong> referral(s)? This cannot be undone.`,
      confirmButtonText: "Send",
      cancelButtonText: "Cancel"
    };

    this.dialog
      .open<boolean>(ConfirmationModalComponent, {
        panelClass: "centered-dialog",
        hasBackdrop: true,
        data
      })
      .closed.pipe(takeUntil(this.onDestroy$))
      .subscribe((confirmed: boolean | undefined): void => {
        if (confirmed) {
          this.executeSend();
        }
      });
  }

  private executeSend(): void {
    this.isSending = true;
    const request: ConsumerSmsCampaignRequest = this.buildRequest(false);
    this.consumerSmsCampaignService
      .runCampaign(request)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (result: ConsumerSmsCampaignResult): void => {
          this.isSending = false;
          this.sendResult = result;
          this.previewResult = null;
          this.toastService.open(
            `Campaign complete — sent: ${result.sent}, failed: ${result.failed}, skipped: ${result.skipped}.`,
            "Close",
            { duration: 9000, panelClass: ["bg-green-600"] }
          );
        },
        error: (error: HttpErrorResponse): void => {
          this.isSending = false;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to send the campaign."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  private extractErrorMessage(error: HttpErrorResponse, fallback: string): string {
    if (error.error && typeof error.error === "object" && error.error.message) {
      return error.error.message;
    }
    if (typeof error.error === "string" && error.error.trim()) {
      return error.error;
    }
    if (error.message) {
      return error.message;
    }
    return fallback;
  }

  private buildRequest(dryRun: boolean): ConsumerSmsCampaignRequest {
    const request: ConsumerSmsCampaignRequest = {
      fromDate: this.fromDate,
      toDate: this.toDate,
      dryRun
    };
    if (this.limit != null && this.limit > 0) {
      request.limit = this.limit;
    }
    if (this.selectedCategory) {
      request.category = this.selectedCategory;
    }
    if (this.selectedLobCodes.length > 0) {
      request.lobCodes = this.selectedLobCodes;
    }
    return request;
  }

  private today(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
